import { useState, useEffect, useMemo, useCallback } from "react";
import { Building2, Users, ChevronDown, ChevronRight, AlertTriangle, AtSign, ToggleLeft, ToggleRight, Crown, StickyNote, Loader2, Globe, Settings, Plus, Trash2, Send, Tag, X, Clock, Mail, MailOpen, MousePointerClick, Video, UserPlus, AlertCircle, Sparkles } from "lucide-react";
import { colors, fonts, CONTACT_TYPES, SECTORS } from "../styles/theme";
import { SearchBar } from "../components/ui";
import { ContactDrawer } from "../components/ContactDrawer";
import { fetchCompanies, fetchCompanyNotes, createCompanyNote, deleteCompanyNote, updateCompany, updateContact, deriveContactType, displayName, initials, setDecisionMaker, fmtDate, fetchSystemSettings, bulkUpdateContacts, replaceTypeTag, getCanonicalTagForType, getAllTypeTags, fetchCompanyTimeline, fetchCompanyEmailSummary, generateCompanyEmailSummary, mergeCompanies } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { DomainManager } from "../components/DomainManager";

const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "hotmail.com",
  "hotmail.co.uk", "outlook.com", "live.com", "live.co.uk", "msn.com",
  "aol.com", "icloud.com", "me.com", "mac.com", "mail.com", "protonmail.com",
  "proton.me", "zoho.com", "ymail.com", "gmx.com", "gmx.co.uk",
  "btinternet.com", "sky.com", "virginmedia.com", "talktalk.net",
]);

function isFreeEmail(email) {
  if (!email) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? FREE_EMAIL_DOMAINS.has(domain) : false;
}

function getCompanyDomain(contacts) {
  for (const c of contacts) {
    if (!c.email) continue;
    const domain = c.email.split("@")[1]?.toLowerCase();
    if (domain && !FREE_EMAIL_DOMAINS.has(domain)) return domain;
  }
  return null;
}

const INACTIVE_TAGS = new Set(["inactive", "unsubscribed from newsletter", "pending unsub"]);

function isInactiveContact(contact) {
  const allTags = [...(contact.tags || []), ...(contact.ghlTags || [])].map((t) => t.toLowerCase());
  if (allTags.some((t) => INACTIVE_TAGS.has(t))) return true;
  const cf = contact.customFields || {};
  const sent = Number(cf.emailsSentCount) || 0;
  if (sent === 0) return false;
  const opens = Number(cf.emailOpensCount) || 0;
  const clicks = Number(cf.emailClicksCount) || 0;
  return opens === 0 && clicks === 0;
}

export default function Companies() {
  const { role } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [hideInactive, setHideInactive] = useState(true);
  const [showDomainManager, setShowDomainManager] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [masterTags, setMasterTags] = useState([]);
  const [masterSectors, setMasterSectors] = useState(SECTORS);

  // Load master company tags and sectors from system settings
  useEffect(() => {
    fetchSystemSettings()
      .then((s) => {
        setMasterTags(s.companyTags || []);
        if (s.sectors?.length) setMasterSectors(s.sectors);
      })
      .catch(() => {});
  }, []);

  const handleTagsChange = async (companyId, addTags, removeTags) => {
    // Optimistic update
    setCompanies((prev) =>
      prev.map((c) => {
        if (c.id !== companyId) return c;
        let tags = [...(c.tags || [])];
        if (removeTags?.length) {
          const removeSet = new Set(removeTags.map((t) => t.toLowerCase()));
          tags = tags.filter((t) => !removeSet.has(t.toLowerCase()));
        }
        if (addTags?.length) tags = [...new Set([...tags, ...addTags])];
        return { ...c, tags };
      }),
    );
    try {
      await updateCompany(companyId, { addTags, removeTags });
    } catch {
      setRefreshKey((k) => k + 1);
    }
  };

  const handleBulkTypeChange = async (company, newTypeId) => {
    if (!newTypeId || !company.contacts?.length) return;
    const typeLabel = (CONTACT_TYPES[newTypeId] || {}).label || newTypeId;
    const ok = window.confirm(
      `Change all ${company.contacts.length} contacts in "${company.name}" to ${typeLabel}?`
    );
    if (!ok) return;

    const contactEntries = company.contacts
      .filter((c) => c.id && c.email)
      .map((c) => ({ id: c.id, email: c.email }));
    if (!contactEntries.length) return;

    // Optimistic update — replace type tags on each contact
    setCompanies((prev) =>
      prev.map((c) => {
        if (c.id !== company.id) return c;
        return {
          ...c,
          contacts: c.contacts.map((ct) => ({
            ...ct,
            tags: replaceTypeTag(ct.tags, newTypeId),
          })),
        };
      }),
    );

    try {
      const newTag = getCanonicalTagForType(newTypeId);
      const allTypeTags = getAllTypeTags();
      await bulkUpdateContacts(contactEntries, {}, [newTag], allTypeTags.filter((t) => t !== newTag));
    } catch (err) {
      console.error("Bulk type change failed:", err);
      setRefreshKey((k) => k + 1);
    }
  };

  const handleRemoveContact = async (contact, companyName) => {
    const name = displayName(contact) || contact.email;
    const ok = window.confirm(
      `Remove "${name}" from ${companyName} and send back to review queue?`
    );
    if (!ok) return;

    // Optimistic update — remove from company's contacts list
    setCompanies((prev) =>
      prev.map((c) => ({
        ...c,
        contacts: c.contacts.filter((ct) => ct.id !== contact.id),
      })),
    );

    try {
      await updateContact(contact.id, {
        email: contact.email,
        companyId: null,
        reviewStatus: "pending",
      });
    } catch (err) {
      console.error("Remove contact failed:", err);
      setRefreshKey((k) => k + 1);
    }
  };

  const handleToggleDecisionMaker = async (contact) => {
    const newVal = !contact.isDecisionMaker;
    // Optimistic update
    setCompanies((prev) =>
      prev.map((c) => ({
        ...c,
        contacts: c.contacts.map((ct) =>
          ct.id === contact.id ? { ...ct, isDecisionMaker: newVal } : ct,
        ),
      })),
    );
    try {
      await setDecisionMaker(contact.id, contact.email, newVal);
    } catch {
      // Revert on failure
      setCompanies((prev) =>
        prev.map((c) => ({
          ...c,
          contacts: c.contacts.map((ct) =>
            ct.id === contact.id ? { ...ct, isDecisionMaker: !newVal } : ct,
          ),
        })),
      );
    }
  };

  const handleSectorChange = async (companyId, sector) => {
    // Optimistic update
    setCompanies((prev) =>
      prev.map((c) => (c.id === companyId ? { ...c, sector } : c)),
    );
    try {
      await updateCompany(companyId, { sector });
    } catch {
      setRefreshKey((k) => k + 1); // reload on failure
    }
  };

  const handleRemoveDomain = async (company, domain) => {
    const ok = window.confirm(
      `Remove @${domain} from "${company.name}"? Contacts using this domain will be unlinked.`
    );
    if (!ok) return;
    // Optimistic update
    setCompanies((prev) =>
      prev.map((c) => {
        if (c.id !== company.id) return c;
        return {
          ...c,
          domains: (c.domains || []).filter((d) => d.toLowerCase() !== domain.toLowerCase()),
          contacts: c.contacts.filter((ct) => {
            const d = (ct.email || "").split("@")[1]?.toLowerCase();
            return d !== domain.toLowerCase();
          }),
        };
      }),
    );
    try {
      await updateCompany(company.id, { removeDomains: [domain] });
    } catch (err) {
      console.error("Remove domain failed:", err);
      setRefreshKey((k) => k + 1);
    }
  };

  const [mergeSource, setMergeSource] = useState(null);

  const handleMerge = async (targetCompany) => {
    if (!mergeSource || mergeSource.id === targetCompany.id) return;
    const ok = window.confirm(
      `Merge "${mergeSource.name}" into "${targetCompany.name}"? All contacts and domains will be moved to "${targetCompany.name}".`
    );
    if (!ok) return;
    try {
      await mergeCompanies(mergeSource.id, targetCompany.id);
      setMergeSource(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Merge failed:", err);
      alert("Merge failed: " + err.message);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const timer = setTimeout(
      () => {
        fetchCompanies({ search })
          .then((data) => {
            if (cancelled) return;
            setCompanies(data.companies || []);
            setTotal(data.total || 0);
          })
          .catch((err) => {
            if (cancelled) return;
            setError(err.message);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      search ? 300 : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, refreshKey]);

  // Filter out inactive contacts when toggle is on
  const visibleCompanies = useMemo(() => {
    if (!hideInactive) return companies;
    return companies
      .map((c) => ({
        ...c,
        contacts: c.contacts.filter((ct) => !isInactiveContact(ct)),
      }))
      .filter((c) => c.contacts.length > 0);
  }, [companies, hideInactive]);

  const totalContacts = useMemo(
    () => visibleCompanies.reduce((sum, c) => sum + c.contacts.length, 0),
    [visibleCompanies],
  );

  const inactiveCount = useMemo(
    () => companies.reduce((sum, c) => sum + c.contacts.filter(isInactiveContact).length, 0),
    [companies],
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Companies
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {loading
              ? "Loading..."
              : `${visibleCompanies.length} companies, ${totalContacts} contacts${hideInactive ? ` (${inactiveCount} inactive hidden)` : ""}`}
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 20 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search companies..."
        />
        <button
          onClick={() => setHideInactive(!hideInactive)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 14px",
            borderRadius: 6,
            border: `1px solid ${hideInactive ? colors.accent : colors.border}`,
            background: hideInactive ? colors.accentDim : "transparent",
            color: hideInactive ? colors.accent : colors.textSecondary,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: fonts.body,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "all 0.15s",
          }}
        >
          {hideInactive
            ? <ToggleRight size={16} />
            : <ToggleLeft size={16} />
          }
          Hide inactive
          {inactiveCount > 0 && (
            <span style={{
              fontSize: 10,
              padding: "1px 6px",
              borderRadius: 3,
              background: hideInactive ? colors.accent : colors.border,
              color: hideInactive ? "#ffffff" : colors.textSecondary,
              fontWeight: 700,
            }}>
              {inactiveCount}
            </span>
          )}
        </button>
        {role === "admin" && (
          <button
            onClick={() => setShowDomainManager(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: "transparent",
              color: colors.textSecondary,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: fonts.body,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
          >
            <Settings size={14} />
            Manage Domains
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: 16,
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8,
            marginBottom: 16,
            color: "#ef4444",
            fontSize: 13,
          }}
        >
          Failed to load companies: {error}
        </div>
      )}

      {mergeSource && (
        <div style={{
          padding: "10px 16px",
          background: "rgba(99,102,241,0.1)",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: 8,
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 13,
          color: "#6366f1",
          fontFamily: fonts.body,
        }}>
          <span>Merging <strong>{mergeSource.name}</strong> — click another company to merge into it</span>
          <button onClick={() => setMergeSource(null)} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visibleCompanies.map((company) => {
          const companyKey = company.id || company.name;
          const isExpanded = expandedCompany === companyKey;
          return (
            <CompanyCard
              key={companyKey}
              company={company}
              isExpanded={isExpanded}
              onToggle={() => {
                if (mergeSource && company.id && mergeSource.id !== company.id) {
                  handleMerge(company);
                  return;
                }
                setExpandedCompany(isExpanded ? null : companyKey);
              }}
              onContactClick={setSelectedContact}
              onToggleDecisionMaker={handleToggleDecisionMaker}
              onSectorChange={handleSectorChange}
              onTagsChange={handleTagsChange}
              onBulkTypeChange={handleBulkTypeChange}
              onRemoveContact={handleRemoveContact}
              onRemoveDomain={handleRemoveDomain}
              onStartMerge={setMergeSource}
              mergeSource={mergeSource}
              masterTags={masterTags}
              masterSectors={masterSectors}
              role={role}
            />
          );
        })}
        {!loading && visibleCompanies.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              color: colors.textSecondary,
              fontSize: 14,
            }}
          >
            No companies found
          </div>
        )}
      </div>

      <ContactDrawer
        contact={selectedContact}
        onClose={() => setSelectedContact(null)}
      />

      {showDomainManager && (
        <DomainManager
          companies={companies}
          onClose={() => setShowDomainManager(false)}
          onSaved={() => {
            setShowDomainManager(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

function CompanyCard({ company, isExpanded, onToggle, onContactClick, onToggleDecisionMaker, onSectorChange, onTagsChange, onBulkTypeChange, onRemoveContact, onRemoveDomain, onStartMerge, mergeSource, masterTags, masterSectors, role }) {
  const sector = company.sector || "";
  const isNoCompany = company.name === "No Company";
  const [logoError, setLogoError] = useState(false);
  const domain = useMemo(
    () => isNoCompany ? null : getCompanyDomain(company.contacts),
    [company.contacts, isNoCompany],
  );
  const contactTypes = useMemo(() => {
    const counts = {};
    for (const c of company.contacts) {
      const type = deriveContactType(c.tags, c.ghlTags);
      counts[type] = (counts[type] || 0) + 1;
    }
    return counts;
  }, [company.contacts]);

  const personalEmailCount = useMemo(
    () => isNoCompany ? 0 : company.contacts.filter((c) => isFreeEmail(c.email)).length,
    [company.contacts, isNoCompany],
  );

  const isMergeTarget = mergeSource && company.id && mergeSource.id !== company.id;

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${isMergeTarget ? "rgba(99,102,241,0.5)" : isNoCompany ? "rgba(234,179,8,0.3)" : colors.border}`,
        borderRadius: 8,
        overflow: "hidden",
        cursor: isMergeTarget ? "pointer" : undefined,
        boxShadow: isMergeTarget ? "0 0 0 2px rgba(99,102,241,0.2)" : undefined,
      }}
    >
      {/* Company header */}
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 20px",
          cursor: "pointer",
          transition: "background 0.1s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = colors.bgHover)
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.background = "transparent")
        }
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: isNoCompany ? "rgba(234,179,8,0.1)" : (domain && !logoError ? "transparent" : colors.accentDim),
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {isNoCompany ? (
            <AlertTriangle size={18} color="#eab308" />
          ) : domain && !logoError ? (
            <img
              src={`https://logo.clearbit.com/${domain}`}
              alt={company.name}
              width={36}
              height={36}
              style={{ objectFit: "contain", borderRadius: 8 }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <Building2 size={18} color={colors.accent} />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: isNoCompany ? "#eab308" : colors.text,
              fontFamily: fonts.heading,
            }}
          >
            {company.name}
            {isNoCompany && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: colors.textSecondary,
                  fontFamily: fonts.body,
                  marginLeft: 8,
                }}
              >
                — needs company assignment
              </span>
            )}
            {!isNoCompany && company.mergedFrom?.length > 1 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: colors.textSecondary,
                  fontFamily: fonts.body,
                  marginLeft: 8,
                }}
              >
                — includes {company.mergedFrom.filter((n) => n !== company.name).join(", ")}
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 12,
              color: colors.textSecondary,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Users size={11} />
            {company.contacts.length}{" "}
            {company.contacts.length === 1 ? "contact" : "contacts"}
          </div>
        </div>

        {/* Type breakdown badges */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
          {Object.entries(contactTypes).map(([type, count]) => {
            const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
            return (
              <span
                key={type}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                  color: typeInfo.color,
                  background: typeInfo.bg,
                  fontFamily: fonts.body,
                }}
              >
                {count} {typeInfo.label}
              </span>
            );
          })}
          {personalEmailCount > 0 && (
            <span
              title={`${personalEmailCount} contact${personalEmailCount > 1 ? "s" : ""} using personal email`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 4,
                color: "#eab308",
                background: "rgba(234,179,8,0.1)",
                fontFamily: fonts.body,
              }}
            >
              <AtSign size={10} />
              {personalEmailCount}
            </span>
          )}
          {company.domains?.length > 1 && (
            <span
              title={`Merged domains: ${company.domains.join(", ")}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 4,
                color: "#6366f1",
                background: "rgba(99,102,241,0.1)",
                fontFamily: fonts.body,
              }}
            >
              <Globe size={10} />
              {company.domains.length} domains
            </span>
          )}
        </div>

        {!isNoCompany && (
          <select
            value={sector}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              onSectorChange?.(company.id, e.target.value);
            }}
            style={{
              padding: "2px 6px",
              fontSize: 10,
              fontWeight: 600,
              fontFamily: fonts.body,
              border: `1px solid ${sector ? "#7C3AED40" : colors.border}`,
              borderRadius: 4,
              background: sector ? "rgba(124,58,237,0.08)" : "transparent",
              color: sector ? "#7C3AED" : colors.textSecondary,
              cursor: "pointer",
              outline: "none",
              flexShrink: 0,
              maxWidth: 140,
            }}
          >
            <option value="">Sector...</option>
            {(masterSectors || SECTORS).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}

        {isExpanded ? (
          <ChevronDown size={16} color={colors.textSecondary} />
        ) : (
          <ChevronRight size={16} color={colors.textSecondary} />
        )}
      </div>

      {/* Expanded contacts list */}
      {isExpanded && (
        <div
          style={{
            borderTop: `1px solid ${colors.border}`,
            padding: "4px 0",
          }}
        >
          {company.id && (
            <div style={{
              padding: "6px 20px 6px 68px",
              fontSize: 10,
              color: colors.textSecondary,
              fontFamily: fonts.mono,
              opacity: 0.6,
              userSelect: "all",
              borderBottom: `1px solid ${colors.border}`,
            }}>
              {company.id}
            </div>
          )}
          {company.domains?.length > 0 && (
            <div style={{
              padding: "8px 20px 8px 68px",
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
              borderBottom: `1px solid ${colors.border}`,
            }}>
              {company.domains.map((d) => (
                <span key={d} style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: colors.accentDim,
                  color: colors.textSecondary,
                  fontFamily: fonts.mono,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}>
                  @{d}
                  {company.domains.length > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onRemoveDomain(company, d); }}
                      title={`Remove @${d} from this company`}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", color: colors.textSecondary, opacity: 0.5 }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = "#ef4444"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.5; e.currentTarget.style.color = colors.textSecondary; }}
                    >
                      <X size={10} />
                    </button>
                  )}
                </span>
              ))}
              {!isNoCompany && company.id && (
                <button
                  onClick={(e) => { e.stopPropagation(); onStartMerge(company); }}
                  title="Merge this company into another"
                  style={{
                    background: "none",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 10,
                    color: colors.textSecondary,
                    cursor: "pointer",
                    fontFamily: fonts.body,
                    fontWeight: 600,
                    opacity: 0.6,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = "#6366f1"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; e.currentTarget.style.color = colors.textSecondary; }}
                >
                  Merge
                </button>
              )}
            </div>
          )}
          {/* Company tags + bulk type change */}
          {!isNoCompany && company.id && (
            <div style={{ borderBottom: `1px solid ${colors.border}`, padding: "8px 20px 8px 68px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <CompanyTagsRow
                companyId={company.id}
                tags={company.tags || []}
                masterTags={masterTags || []}
                onTagsChange={onTagsChange}
              />
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) onBulkTypeChange?.(company, e.target.value);
                  e.target.value = "";
                }}
                style={{
                  padding: "3px 8px",
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 4,
                  background: "transparent",
                  color: colors.textSecondary,
                  cursor: "pointer",
                  outline: "none",
                  flexShrink: 0,
                }}
              >
                <option value="">Set all contacts as...</option>
                {Object.entries(CONTACT_TYPES).map(([id, info]) => (
                  <option key={id} value={id}>{info.label}</option>
                ))}
              </select>
            </div>
          )}
          {company.contacts.map((contact) => {
            const type = deriveContactType(contact.tags, contact.ghlTags);
            const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
            const personalEmail = !isNoCompany && isFreeEmail(contact.email);
            const inactive = isInactiveContact(contact);
            return (
              <div
                key={contact.id || contact.email}
                onClick={() => onContactClick?.(contact)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 20px 10px 68px",
                  cursor: "pointer",
                  transition: "background 0.1s",
                  opacity: inactive ? 0.45 : 1,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = colors.bgHover)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: typeInfo.bg,
                    color: typeInfo.color,
                    fontFamily: fonts.heading,
                    fontWeight: 700,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  {initials(contact)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>
                    {displayName(contact)}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 11,
                      color: colors.textSecondary,
                      fontFamily: fonts.mono,
                    }}
                  >
                    {contact.email}
                    {personalEmail && (
                      <span title="Personal email — no corporate address on file" style={{ display: "inline-flex", alignItems: "center" }}>
                        <AtSign size={11} color="#eab308" />
                      </span>
                    )}
                  </div>
                  {contact.id && (
                    <div
                      style={{
                        fontSize: 9,
                        color: colors.textSecondary,
                        fontFamily: fonts.mono,
                        opacity: 0.6,
                        userSelect: "all",
                      }}
                    >
                      {contact.id}
                    </div>
                  )}
                </div>
                {contact.customFields?.jobTitle && (
                  <span
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      flexShrink: 0,
                    }}
                  >
                    {contact.customFields.jobTitle}
                  </span>
                )}
                {contact.createdAt && (
                  <span
                    style={{
                      fontSize: 10,
                      color: colors.textSecondary,
                      fontFamily: fonts.mono,
                      flexShrink: 0,
                    }}
                  >
                    {fmtDate(contact.createdAt)}
                  </span>
                )}
                <button
                  title={contact.isDecisionMaker ? "Remove as decision maker" : "Set as decision maker"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDecisionMaker?.(contact);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 26,
                    height: 26,
                    borderRadius: 5,
                    border: "none",
                    background: contact.isDecisionMaker ? "rgba(245,158,11,0.15)" : "transparent",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "all 0.15s",
                  }}
                >
                  <Crown
                    size={14}
                    color={contact.isDecisionMaker ? "#f59e0b" : colors.border}
                    fill={contact.isDecisionMaker ? "#f59e0b" : "none"}
                  />
                </button>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: 4,
                    color: typeInfo.color,
                    background: typeInfo.bg,
                    fontFamily: fonts.body,
                    flexShrink: 0,
                  }}
                >
                  {typeInfo.label}
                </span>
                {!isNoCompany && (
                  <button
                    title="Remove from company and send to review"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveContact?.(contact, company.name);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: 4,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      flexShrink: 0,
                      opacity: 0.4,
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.background = "rgba(239,68,68,0.1)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.4"; e.currentTarget.style.background = "transparent"; }}
                  >
                    <X size={12} color="#ef4444" />
                  </button>
                )}
              </div>
            );
          })}
          {!isNoCompany && company.id && <CompanyTimeline companyId={company.id} role={role} />}
          {!isNoCompany && company.id && <CompanyNotes companyId={company.id} />}
        </div>
      )}
    </div>
  );
}

function CompanyTagsRow({ companyId, tags, masterTags, onTagsChange }) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const existingSet = new Set((tags || []).map((t) => t.toLowerCase()));
  const suggestions = (masterTags || []).filter(
    (t) => !existingSet.has(t.toLowerCase()) && t.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const addTag = (tag) => {
    if (!tag.trim() || existingSet.has(tag.trim().toLowerCase())) return;
    onTagsChange?.(companyId, [tag.trim()], []);
    setInputValue("");
    setShowSuggestions(false);
  };

  const removeTag = (tag) => {
    onTagsChange?.(companyId, [], [tag]);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6,
        flex: 1,
      }}
    >
      <Tag size={12} color={colors.textSecondary} style={{ flexShrink: 0 }} />
      {tags.map((tag) => (
        <span
          key={tag}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 4,
            background: "rgba(16,185,129,0.08)",
            color: "#10b981",
            fontWeight: 600,
            fontFamily: fonts.body,
          }}
        >
          {tag}
          <button
            onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              color: "#10b981",
            }}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <div style={{ position: "relative" }}>
        <input
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && inputValue.trim()) {
              e.preventDefault();
              addTag(inputValue);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder={tags.length === 0 ? "Add tag..." : "+"}
          style={{
            padding: "2px 8px",
            fontSize: 11,
            border: `1px solid ${colors.border}`,
            borderRadius: 4,
            background: "transparent",
            color: colors.text,
            outline: "none",
            width: tags.length === 0 ? 100 : 60,
            fontFamily: fonts.body,
          }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              marginTop: 4,
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 100,
              maxHeight: 160,
              overflowY: "auto",
              minWidth: 140,
            }}
          >
            {suggestions.map((s) => (
              <button
                key={s}
                onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "6px 12px",
                  border: "none",
                  background: "transparent",
                  textAlign: "left",
                  fontSize: 12,
                  color: colors.text,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TIMELINE_ICONS = {
  email_sent: Mail,
  email_received: MailOpen,
  open: MailOpen,
  click: MousePointerClick,
  newsletter_open: MailOpen,
  newsletter_click: MousePointerClick,
  webcast_registered: UserPlus,
  webcast_attended: Video,
  note: StickyNote,
  created: UserPlus,
  unsubscribed: AlertCircle,
  delivered: Mail,
  bounce: AlertCircle,
  dropped: AlertCircle,
  spam_report: AlertCircle,
};

const TIMELINE_COLORS = {
  email_sent: "#3b82f6",
  email_received: "#8b5cf6",
  open: "#10b981",
  click: "#f59e0b",
  newsletter_open: "#10b981",
  newsletter_click: "#f59e0b",
  webcast_registered: "#6366f1",
  webcast_attended: "#06b6d4",
  note: "#f59e0b",
  created: "#10b981",
  unsubscribed: "#ef4444",
  delivered: "#6b7280",
  bounce: "#ef4444",
  dropped: "#ef4444",
  spam_report: "#ef4444",
};

function CompanyTimeline({ companyId, role }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  const canSummarize = role === "admin" || role === "exec";

  const loadTimeline = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const offset = append ? events.length : 0;
      const data = await fetchCompanyTimeline(companyId, { limit: 30, offset });
      if (append) {
        setEvents((prev) => [...prev, ...data.events]);
      } else {
        setEvents(data.events);
      }
      setTotal(data.total);
      setHasMore(data.hasMore);
    } catch {
      if (!append) setEvents([]);
    }
    setLoading(false);
  }, [companyId, events.length]);

  useEffect(() => {
    if (expanded && events.length === 0) loadTimeline();
    // Load cached summary when expanding (if allowed)
    if (expanded && canSummarize && summary === null) {
      fetchCompanyEmailSummary(companyId)
        .then((data) => { if (data.summary) setSummary(data); })
        .catch(() => {});
    }
  }, [expanded]);

  const handleGenerateSummary = async () => {
    setSummaryLoading(true);
    try {
      const data = await generateCompanyEmailSummary(companyId);
      setSummary(data);
      setSummaryExpanded(true);
    } catch {}
    setSummaryLoading(false);
  };

  const Icon = (type) => TIMELINE_ICONS[type] || Clock;
  const iconColor = (type) => TIMELINE_COLORS[type] || colors.textSecondary;

  return (
    <div
      style={{
        borderTop: `1px solid ${colors.border}`,
        padding: "0 20px 0 68px",
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "10px 0",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: colors.textSecondary,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontFamily: fonts.body,
          width: "100%",
        }}
      >
        <Clock size={12} />
        Activity Timeline {total > 0 && `(${total})`}
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {loading && (
          <Loader2
            size={12}
            className="spin" style={{ marginLeft: 4 }}
          />
        )}
      </button>

      {expanded && (
        <div style={{ paddingBottom: 12 }}>
          {/* AI Summary — exec/admin only */}
          {canSummarize && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); handleGenerateSummary(); }}
                  disabled={summaryLoading}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 6,
                    border: `1px solid ${colors.border}`,
                    background: summaryLoading ? colors.border : "rgba(139,92,246,0.08)",
                    color: summaryLoading ? colors.textSecondary : "#8b5cf6",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: summaryLoading ? "default" : "pointer",
                    fontFamily: fonts.body,
                  }}
                >
                  {summaryLoading ? (
                    <>
                      <Loader2 size={12} className="spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={12} />
                      {summary ? "Regenerate AI Summary" : "Summarize with AI"}
                    </>
                  )}
                </button>
                {summary && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setSummaryExpanded(!summaryExpanded); }}
                    style={{
                      background: "none",
                      border: "none",
                      color: colors.textSecondary,
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: fonts.body,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {summaryExpanded ? "Hide" : "Show"} summary
                    {summaryExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  </button>
                )}
              </div>
              {summary && summaryExpanded && (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 6,
                    background: "rgba(139,92,246,0.04)",
                    border: `1px solid rgba(139,92,246,0.15)`,
                    fontSize: 12,
                    color: colors.text,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {summary.summary}
                  {summary.generatedAt && (
                    <div style={{ marginTop: 8, fontSize: 10, color: colors.textSecondary, fontFamily: fonts.mono }}>
                      Generated {timeAgo(summary.generatedAt)}
                      {summary.emailCount ? ` · ${summary.emailCount} emails analysed` : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {events.length === 0 && !loading && (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: "4px 0" }}>
              No activity recorded yet.
            </div>
          )}

          {events.map((evt, i) => {
            const EvtIcon = Icon(evt.type);
            const color = iconColor(evt.type);
            return (
              <div
                key={evt.id}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "6px 0",
                  borderBottom: i < events.length - 1 ? `1px solid ${colors.borderLight}` : "none",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: `${color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  <EvtIcon size={11} color={color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: colors.text, lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 600 }}>{evt.contactName}</span>
                    {" — "}
                    {evt.description}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: colors.textSecondary,
                      fontFamily: fonts.mono,
                      marginTop: 1,
                    }}
                  >
                    {timeAgo(evt.timestamp)}
                  </div>
                </div>
              </div>
            );
          })}

          {hasMore && !loading && (
            <button
              onClick={(e) => { e.stopPropagation(); loadTimeline(true); }}
              style={{
                display: "block",
                width: "100%",
                padding: "8px 0",
                marginTop: 4,
                background: "none",
                border: `1px solid ${colors.border}`,
                borderRadius: 5,
                color: colors.accent,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: fonts.body,
              }}
            >
              Load more ({total - events.length} remaining)
            </button>
          )}
        </div>
      )}    </div>
  );
}

function CompanyNotes({ companyId }) {
  const [companyNotes, setCompanyNotes] = useState([]);
  const [contactNotes, setContactNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const loadNotes = () => {
    setLoading(true);
    fetchCompanyNotes(companyId)
      .then((data) => {
        setCompanyNotes(data.companyNotes || []);
        setContactNotes(data.contactNotes || []);
      })
      .catch(() => {
        setCompanyNotes([]);
        setContactNotes([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNotes();
  }, [companyId]);

  const handleAddNote = async () => {
    if (!newNote.trim() || saving) return;
    setSaving(true);
    try {
      await createCompanyNote(companyId, newNote.trim());
      setNewNote("");
      loadNotes();
    } catch {}
    setSaving(false);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteCompanyNote(companyId, noteId);
      setCompanyNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch {}
  };

  if (loading) {
    return (
      <div style={{ padding: "12px 20px 12px 68px" }}>
        <Loader2
          size={14}
          color={colors.textSecondary}
          className="spin"
        />      </div>
    );
  }

  const hasNotes = companyNotes.length > 0 || contactNotes.length > 0;

  return (
    <div
      style={{
        borderTop: `1px solid ${colors.border}`,
        padding: "12px 20px 12px 68px",
      }}
    >
      {/* Add note input */}
      <div style={{ display: "flex", gap: 6, marginBottom: hasNotes ? 10 : 0 }}>
        <input
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
          onClick={(e) => e.stopPropagation()}
          placeholder="Add a company note..."
          style={{
            flex: 1,
            padding: "6px 10px",
            fontSize: 12,
            fontFamily: fonts.body,
            border: `1px solid ${colors.border}`,
            borderRadius: 5,
            background: colors.bgCard,
            color: colors.text,
            outline: "none",
          }}
        />
        <button
          onClick={(e) => { e.stopPropagation(); handleAddNote(); }}
          disabled={!newNote.trim() || saving}
          style={{
            padding: "6px 10px",
            borderRadius: 5,
            border: `1px solid ${colors.border}`,
            background: newNote.trim() ? colors.accentDim : "transparent",
            color: newNote.trim() ? colors.accent : colors.textSecondary,
            cursor: newNote.trim() ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Send size={12} />
        </button>
      </div>

      {/* Company-level notes */}
      {companyNotes.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: colors.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 6,
            }}
          >
            <StickyNote size={12} />
            Company Notes ({companyNotes.length})
          </div>
          {companyNotes.map((note, i) => (
            <div
              key={note.id}
              style={{
                padding: "6px 0",
                borderBottom:
                  i < companyNotes.length - 1
                    ? `1px solid ${colors.borderLight}`
                    : "none",
                display: "flex",
                gap: 8,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: colors.text,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    marginBottom: 2,
                  }}
                >
                  {note.text}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: colors.textSecondary,
                    fontFamily: fonts.mono,
                  }}
                >
                  {note.author} · {timeAgo(note.createdAt)}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteNote(note.id);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 2,
                  opacity: 0.4,
                  flexShrink: 0,
                  alignSelf: "flex-start",
                }}
                title="Delete note"
              >
                <Trash2 size={12} color={colors.textSecondary} />
              </button>
            </div>
          ))}
        </>
      )}

      {/* Contact notes */}
      {contactNotes.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: colors.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 6,
              marginTop: companyNotes.length > 0 ? 10 : 0,
            }}
          >
            <StickyNote size={12} />
            Contact Notes ({contactNotes.length})
          </div>
          {contactNotes.map((note, i) => (
            <div
              key={note.id}
              style={{
                padding: "6px 0",
                borderBottom:
                  i < contactNotes.length - 1
                    ? `1px solid ${colors.borderLight}`
                    : "none",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: colors.text,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  marginBottom: 2,
                }}
              >
                {note.text}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: colors.textSecondary,
                  fontFamily: fonts.mono,
                }}
              >
                {note.contactName} · {note.author} · {timeAgo(note.createdAt)}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(iso);
}
